import axios from 'axios';

export const API_BASE_URL = process.env.REACT_APP_API_URL || '/api/v1';

const api = axios.create({
  baseURL: API_BASE_URL,
  paramsSerializer: {
    serialize: (params) => {
      // 自定义参数序列化，处理数组参数
      const searchParams = new URLSearchParams();
      
      Object.entries(params).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          // 对于数组，为每个元素添加同名参数
          value.forEach(item => {
            searchParams.append(key, item);
          });
        } else if (value !== null && value !== undefined) {
          searchParams.append(key, value);
        }
      });
      
      return searchParams.toString();
    }
  }
});

// 请求拦截器，添加认证token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 响应拦截器，处理错误
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      // 只有当前不在登录页面时才跳转
      if (!window.location.pathname.includes('/login') && window.location.pathname !== '/') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  googleLogin: (data) => api.post('/auth/google', data),
  getProfile: () => api.get('/auth/me'),
  refresh: () => api.post('/auth/refresh'),
};

export const registerAPI = {
  sendVerificationCode: (email) => api.post('/register/send-code', { email }),
  register: (data) => api.post('/register', data),
};

export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
};

export const systemAPI = {
  getHealth: () => api.get('/system/health'),
  getDbPoolMetrics: () => api.get('/system/db-pool-metrics'),
};

export const categoryAPI = {
  getAll: () => api.get('/categories'),
  create: (data) => api.post('/categories', data),
  update: (id, data) => api.put(`/categories/${id}`, data),
  delete: (id) => api.delete(`/categories/${id}`),
  batchSetFreightChannels: (categoryId, freightChannelIds) => 
    api.post(`/categories/${categoryId}/batch-set-freight-channels`, { freight_channel_ids: freightChannelIds }),
  getMultiLevel: () => api.get('/categories/multi-level'),
  updateMultiLevel: (data) => api.put('/categories/multi-level', { data }),
};

export const productAPI = {
  getAll: (params = {}) => api.get('/products', { params }),
  getById: (id, params = {}) => api.get(`/products/public/${id}`, { params }), // 获取单个商品详情，公开API
  getByIdAuth: (id, params = {}) => api.get(`/products/${id}`, { params }), // 获取单个商品详情，需要认证，会应用特殊价格
  getPublic: (params = {}) => api.get('/products/public', { params }), // 公开API，无需认证
  create: (data) => api.post('/products', data, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
  update: (id, data) => api.put(`/products/${id}`, data, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
  delete: (id) => api.delete(`/products/${id}`),
  getImages: (id) => api.get(`/products/${id}/images`),
  deleteImage: (productId, imageId) => api.delete(`/products/${productId}/images/${imageId}`),
  deleteDescriptionImage: (productId, imageId) => api.delete(`/products/${productId}/description_images/${imageId}`),
  setCoverImage: (productId, imageId) => api.put(`/products/${productId}/cover/${imageId}`),
  getNotes: (id) => api.get(`/products/${id}/notes`),
  updateNotes: (id, data) => api.post(`/products/${id}/notes`, data),
  getSubheader: (id) => api.get(`/products/${id}/subheader`),
  updateSubheader: (id, data) => api.post(`/products/${id}/subheader`, data),
  appendNotesBatch: (data) => api.put('/products/batch/notes', data),
  clearNotesBatch: (data) => api.delete('/products/batch/notes', { data }),
  setSubheaderBatch: (data) => api.put('/products/batch/subheader', data),
  // 批量操作
  productsBatchDelete: (data) => api.post('/products/batch/delete', data),
  productsBatchUpdateStatus: (data) => api.put('/products/batch/status', data),
  productsBatchUpdateCategory: (data) => api.put('/products/batch/category', data),
  productsBatchUpdateFreight: (data) => api.put('/products/batch/freight-channels', data),
};

export const orderAPI = {
  getAll: (params = {}) => api.get('/orders', { params }),
  getByNumber: (orderNumber) => api.get(`/orders/${orderNumber}`),
  create: (data) => api.post('/orders', data),
  update: (orderNumber, data) => api.put(`/orders/${orderNumber}`, data),
  edit: (orderNumber, data) => api.put(`/orders/${orderNumber}/edit`, data),
  delete: (orderNumber) => api.delete(`/orders/${orderNumber}`),
  // 订单支付接口（支持单个或批量）
  pay: (orderNumbers) => {
    // 统一处理：如果传入单个订单号字符串，转为数组
    const numbers = Array.isArray(orderNumbers) ? orderNumbers : [orderNumbers];
    return api.post('/orders/pay', { order_numbers: numbers });
  },
  cancel: (orderNumber) => api.put(`/orders/${orderNumber}/cancel`),
  updatePrice: (orderNumber, data) => api.put(`/orders/${orderNumber}/price`, data),
  updateFulfillment: (orderNumber, items) => api.put(`/orders/${orderNumber}/fulfillment`, items),
  requestRMA: (orderNumber, items) => api.put(`/orders/${orderNumber}/rma`, items),
  updateRMA: (orderNumber, items) => api.put(`/orders/${orderNumber}/rma/update`, items),
  updateMessage: (orderNumber, message) => api.put(`/orders/${orderNumber}/message`, { message }),
  clearWarning: (orderNumber) => api.put(`/orders/${orderNumber}/clear-warning`),
};

export const walletAPI = {
  getMyWallet: () => api.get('/wallets/me'),
  getMyTransactions: (params = {}) => api.get('/wallets/me/transactions', { params }),
  listWallets: (params = {}) => api.get('/wallets', { params }),
  getWallet: (walletId) => api.get(`/wallets/${walletId}`),
  getWalletTransactions: (walletId, params = {}) => api.get(`/wallets/${walletId}/transactions`, { params }),
  createWalletTransaction: (walletId, data) => api.post(`/wallets/${walletId}/transactions`, data),
};

export const paypalAPI = {
  getConfig: () => api.get('/paypal/config'),
  createOrder: (data) => api.post('/paypal/orders', data),
  captureOrder: (orderId) => api.post(`/paypal/orders/${orderId}/capture`),
};

export const stripeAPI = {
  getConfig: () => api.get('/stripe/config'),
  createCheckoutSession: (data) => api.post('/stripe/checkout-session', data),
};

// 用户管理API (仅Admin)
export const userAPI = {
  getAll: (params = {}) => api.get('/users', { params }),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  impersonate: (userId) => api.post('/auth/impersonate', { user_id: userId }),
  // Profile management (for all users)
  getProfile: () => api.get('/users/profile'),
  updateProfile: (data) => api.put('/users/profile', data),
};

// Dropshipper产品管理API
export const dropshipperAPI = {
  getProducts: (params = {}) => api.get('/dropshipper/products', { params }),
  getProductById: (id) => api.get(`/dropshipper/products/${id}`),
  getFreightChannels: () => api.get('/dropshipper/freight-channels'),
  getMyCustomizations: (userId = null) => {
    const params = userId ? { user_id: userId } : {};
    return api.get('/dropshipper/my-customizations', { params });
  },
  addProduct: (data) => api.post('/dropshipper/products', data),
  updateProduct: (id, data) => api.put(`/dropshipper/products/${id}`, data),
  deleteProduct: (id) => api.delete(`/dropshipper/products/${id}`),
  batchDeleteProducts: (ids) => api.post('/dropshipper/products/batch-delete', { ids }),
};

export const unmappedOrdersAPI = {
  getAll: (params = {}) => api.get('/unmapped-orders', { params }),
  createOrder: (unmappedOrderId, data) => api.post(`/unmapped-orders/${unmappedOrderId}/create-order`, data),
  delete: (unmappedOrderId) => api.delete(`/unmapped-orders/${unmappedOrderId}`),
  batchDelete: (ids) => api.post('/unmapped-orders/batch-delete', { ids }),
};

// 定制需求管理API
export const customizationAPI = {
  getAll: (params) => api.get('/customizations', { params }),
  getById: (id) => api.get(`/customizations/${id}`),
  create: (data) => api.post('/customizations', data),
  update: (id, data) => api.put(`/customizations/${id}`, data),
  delete: (id) => api.delete(`/customizations/${id}`),
  addComment: (customizationId, formData) => api.post(`/customizations/${customizationId}/comments`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
  downloadAttachment: (customizationId, commentId, filename) => api.get(`/customizations/${customizationId}/comments/${commentId}/attachments/${filename}`, {
    params: { download: true },
    responseType: 'blob',
  }),
};

// 支持工单管理API
export const supportTicketAPI = {
  getAll: (params) => api.get('/support-tickets', { params }),
  getById: (id) => api.get(`/support-tickets/${id}`),
  create: (data) => api.post('/support-tickets', data),
  update: (id, data) => api.put(`/support-tickets/${id}`, data),
  delete: (id) => api.delete(`/support-tickets/${id}`),
  addComment: (ticketId, formData) =>
    api.post(`/support-tickets/${ticketId}/comments`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }),
  downloadAttachment: (ticketId, commentId, filename) =>
    api.get(`/support-tickets/${ticketId}/comments/${commentId}/attachments/${filename}`, {
      params: { download: true },
      responseType: 'blob',
    }),
};

export const sourcingRequestAPI = {
  submit: (formData) =>
    api.post('/sourcing-requests', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }),
};

// 运费模板管理API
export const freightAPI = {
  getAll: () => api.get('/freight'),
  getById: (id) => api.get(`/freight/${id}`),
  create: (data) => api.post('/freight', data),
  update: (id, data) => api.put(`/freight/${id}`, data),
  updateAvailableUsers: (id, data) => api.put(`/freight/${id}/available-users`, data),
  delete: (id) => api.delete(`/freight/${id}`),
};

export const shippingCompanyAPI = {
  getAll: () => api.get('/shipping-companies'),
  create: (data) => api.post('/shipping-companies', data),
  update: (id, data) => api.put(`/shipping-companies/${id}`, data),
  delete: (id) => api.delete(`/shipping-companies/${id}`),
};

// 地址管理API
export const addressAPI = {
  getAll: () => api.get('/addresses'),
  getById: (id) => api.get(`/addresses/${id}`),
  getUserAddresses: (userId) => api.get(`/addresses/user/${userId}`), // Admin only: get addresses for specific user
  create: (data) => api.post('/addresses', data),
  createForUser: (userId, data) => api.post(`/addresses/user/${userId}`, data), // Admin only: create address for specific user
  update: (id, data) => api.put(`/addresses/${id}`, data),
  delete: (id) => api.delete(`/addresses/${id}`),
  setDefault: (id) => api.post(`/addresses/${id}/set-default`),
};

// 汇率管理API
export const exchangeRateAPI = {
  getAll: () => api.get('/exchange-rates'),
  getById: (id) => api.get(`/exchange-rates/${id}`),
  getByCurrency: (currencyCode) => api.get(`/exchange-rates/currency/${currencyCode}`),
  create: (data) => api.post('/exchange-rates', data),
  update: (id, data) => api.put(`/exchange-rates/${id}`, data),
  delete: (id) => api.delete(`/exchange-rates/${id}`),
  // 获取可用货币列表（USD + 汇率表中的货币）
  getAvailableCurrencies: async () => {
    const response = await api.get('/exchange-rates');
    const currencies = response.data.map(rate => ({
      code: rate.currency_code,
      rate: rate.rate_to_usd
    }));
    // 添加USD
    currencies.unshift({ code: 'USD', rate: 1.0 });
    return { data: currencies };
  },
};

// 商品集管理API
export const collectionAPI = {
  getAll: () => api.get('/collections'),
  getById: (id) => api.get(`/collections/${id}`),
  create: (data) => api.post('/collections', data),
  update: (id, data) => api.put(`/collections/${id}`, data),
  delete: (id) => api.delete(`/collections/${id}`),
  // 商品集中的商品管理
  getProducts: (id) => api.get(`/collections/${id}/products`),
  addProduct: (id, productId) => api.post(`/collections/${id}/products`, { product_id: productId }),
  updateProducts: (id, productIds) => api.put(`/collections/${id}/products`, { product_ids: productIds }),
  removeProduct: (id, productId) => api.delete(`/collections/${id}/products/${productId}`),
  // 查询多个商品集的商品（分页）
  queryProducts: (collectionIds, params = {}) => api.get('/collections/products/query', { 
    params: { 
      collection_ids: collectionIds,
      ...params 
    } 
  }),
  // 多级商品集分类
  getMultiLevel: () => api.get('/collections/multi-level'),
  updateMultiLevel: (data) => api.put('/collections/multi-level', { data }),
};

// External Store Integration API (supports Shopify, WooCommerce, etc.)
export const externalStoreAPI = {
  getProducts: (platform, shopDomain, params = {}) =>
    api.get(`/external_store/${platform}/${shopDomain}/products`, { params }),
  getUnfulfilledOrders: (platform, shopDomain, params = {}) =>
    api.get(`/external_store/${platform}/${shopDomain}/orders`, { params }),
  importOrders: (platform, shopDomain, data) =>
    api.post(`/external_store/${platform}/${shopDomain}/orders/import`, data),
  syncProduct: (data) => api.post('/external_store/sync-product', data),
  getStoreInfo: (platform, shopDomain) =>
    api.get(`/external_store/${platform}/${shopDomain}/info`),
};

// WooCommerce specific API
export const woocommerceAPI = {
  connectStore: (data) => api.post('/stores/integration/woocommerce', data),
};

export const manualProductConnectionAPI = {
  // External store products list (通过 external_store router)
  getExternalProducts: (platform, shopDomain, params = {}) => 
    externalStoreAPI.getProducts(platform, shopDomain, params),
  // For backward compatibility with Shopify
  getShopifyProducts: (shopDomain, params = {}) => 
    externalStoreAPI.getProducts('shopify', shopDomain, params),
  // Manual connection CRUD（通过manual_connect router）
  create: (data) => api.post('/manual-connect', data),
  getAll: (params = {}) => api.get('/manual-connect', { params }),
  getById: (id) => api.get(`/manual-connect/${id}`),
  update: (id, data) => api.put(`/manual-connect/${id}`, data),
  delete: (id) => api.delete(`/manual-connect/${id}`),
};

export const shopifyIntegrationAPI = {
  // For backward compatibility, these now call externalStoreAPI
  getUnfulfilledOrders: (shopDomain, params = {}) =>
    externalStoreAPI.getUnfulfilledOrders('shopify', shopDomain, params),
  importOrders: (shopDomain, data) =>
    externalStoreAPI.importOrders('shopify', shopDomain, data),
};

export default api;
